import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { TimerStatus } from 'shovel-lib/types';
import { reportAction } from '@api/activityLogApi';
import { getActiveTimer } from '@state/timer/selectors';
import { RootState } from '@state/types';
import { pulseTimerPill } from '@utils/calendar/markUsedCelebration';
import { playChime, primeChimeOnFirstClick } from '@utils/chime';
import { isMobileApp } from '@utils/screenUtils';
import { playsTimerChime, showsTimerNotice } from '@utils/settingsUtils';
import { AlertMoment, alertMoments, AlertTimer, alertWords, momentAction, stillAlerted } from '@utils/timerAlerts';
import { makeAlertClock } from '@utils/timerAlertClock';
import {
  answerTabsInView,
  claimMoment,
  otherTabInView,
  readAlerted,
  shovelInView,
  whenInView,
  writeAlerted
} from '@utils/timerAlertTabs';
import { closeTimerNotices, noteTimerStarted, onNoticeClick, prepareNotices, showTimerNotice } from '@utils/timerNotice';
import { useTimerCheckIns } from './useTimerCheckIns';

// A tab says it's in view as its window starts coming forward: a moment for it to get there before the pill pulses.
const COMES_FORWARD_MS = 250;

/**
 * The chime and desktop notice (timer-alerts spec, Petr 2026-10-31): at the plan's end and at each "Still studying?",
 * the moments the pill asks, so a student in another tab, or with their eyes on a book, hears and can answer before
 * the timer pauses itself. The chime plays wherever they are, like a kitchen timer; the notice only while Shovel is
 * out of view, as the pill asks the same in view. One alert across tabs, and late after a sleeping laptop only as a
 * quiet notice.
 */
export function useTimerAlerts() {
  const timer = useSelector(getActiveTimer);
  const settings = useSelector((state: RootState) => state.settings.userSettings);
  const chimeOn = playsTimerChime(settings);
  const noticeOn = showsTimerNotice(settings);
  const plannedTaskId = timer ? Number(timer.plannedTaskId) : 0;
  const ticking = timer?.status === TimerStatus.ACTIVE;
  const { answered } = useTimerCheckIns(plannedTaskId || undefined);

  const latest = useRef({ timer, chimeOn, noticeOn, answered });
  latest.current = { timer, chimeOn, noticeOn, answered };
  const moments = useRef<AlertMoment[]>([]);

  useEffect(() => (isMobileApp ? undefined : answerTabsInView()), []);
  useEffect(() => {
    if (!isMobileApp) prepareNotices();
  }, []);
  useEffect(() => (chimeOn && !isMobileApp ? primeChimeOnFirstClick() : undefined), [chimeOn]);

  // A notice goes once its moment is dealt with: answered, paused, logged, or another timer started.
  const shown = useRef<number | null>(null);
  useEffect(() => {
    if (shown.current && (shown.current !== plannedTaskId || !ticking)) closeTimerNotices(shown.current);
    shown.current = plannedTaskId || null;
  }, [plannedTaskId, ticking]);
  const lastAnswered = useRef(answered);
  useEffect(() => {
    if (plannedTaskId && answered > lastAnswered.current) closeTimerNotices(plannedTaskId);
    lastAnswered.current = answered;
  }, [answered, plannedTaskId]);

  // A fresh start, for the ask's "Not now" (hidden for the next few timers started).
  const counted = useRef<number | null>(null);
  useEffect(() => {
    if (!plannedTaskId || counted.current === plannedTaskId) return;
    counted.current = plannedTaskId;
    if ((timer?.timePassedInSeconds || 0) < 60 && ticking) noteTimerStarted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannedTaskId, ticking]);

  // A click on the notice, from its worker (public/timer-notice-sw.js) or the plain one: Shovel comes forward and the
  // pill pulses as it is, asking or not. The student opens it if they want to: the session's popup opening by itself
  // hid what the pill was asking (Petr, 2026-11-01).
  const noticeClicked = useCallback((id: number) => {
    const now = latest.current.timer;
    if (!now || Number(now.plannedTaskId) !== id) return;
    reportAction('timer_alert_clicked');
    whenInView(() => window.setTimeout(pulseTimerPill, COMES_FORWARD_MS));
  }, []);

  const clock = useRef<ReturnType<typeof makeAlertClock> | null>(null);
  useEffect(() => {
    if (isMobileApp) return;
    const alertTimerOf = (now: typeof latest.current): AlertTimer | null =>
      now.timer
        ? {
            timePassedInSeconds: now.timer.timePassedInSeconds || 0,
            startedInUtc: now.timer.startedInUtc,
            planSeconds: (now.timer.plannedMinutes || 0) * 60,
            answered: now.answered
          }
        : null;

    const alert = async (key: string) => {
      const [id, checkIn] = key.split(':').map(Number);
      const now = latest.current;
      const alertTimer = alertTimerOf(now);
      const moment = moments.current.find(each => each.checkIn === checkIn);
      if (!now.timer || !alertTimer || Number(now.timer.plannedTaskId) !== id || !moment) return;
      if (now.timer.status !== TimerStatus.ACTIVE) return;
      const action = momentAction(moment, alertTimer, Date.now());
      if (action === 'skip') {
        await claimMoment(id, checkIn);
        return;
      }
      // A Shovel tab in view chimes itself: a hidden one leaves the moment to it.
      const inView = shovelInView();
      if (!inView && (await otherTabInView())) return;
      if (!(await claimMoment(id, checkIn))) return;
      const late = action === 'late';
      const chimed = now.chimeOn && !late ? await playChime(checkIn >= 1 ? 'checkIn' : 'timesUp') : false;
      const words = alertWords(moment, now.timer.title, Date.now() - moment.at);
      // In view, the pill asks the same: no notice over it (Petr, 2026-10-31).
      const noticed =
        now.noticeOn && !inView
          ? await showTimerNotice(
              { plannedTaskId: id, checkIn, title: words.title, body: words.body, silent: chimed || late },
              () => noticeClicked(id)
            )
          : false;
      if (chimed || noticed) {
        reportAction('timer_alert_shown', {
          moment: checkIn >= 1 ? 'check_in' : 'end',
          chime: chimed,
          notice: noticed,
          late,
          inView
        });
      }
    };

    clock.current = makeAlertClock(key => {
      alert(key);
    });
    return () => {
      clock.current?.stop();
      clock.current = null;
    };
  }, [noticeClicked]);

  const startedInUtc = timer?.startedInUtc ? new Date(timer.startedInUtc as any).getTime() : 0;
  const timePassed = timer?.timePassedInSeconds || 0;
  const plannedMinutes = timer?.plannedMinutes || 0;
  const sessionIsUsed = !!timer?.sessionIsUsed;
  useEffect(() => {
    const set = clock.current?.set;
    if (!set) return;
    const both = latest.current.chimeOn || latest.current.noticeOn;
    if (!plannedTaskId || !ticking || !plannedMinutes || sessionIsUsed || !both) {
      moments.current = [];
      set([]);
      return;
    }
    const alertTimer: AlertTimer = {
      timePassedInSeconds: timePassed,
      startedInUtc: new Date(startedInUtc),
      planSeconds: plannedMinutes * 60,
      answered
    };
    const now = Date.now();
    const kept = readAlerted(plannedTaskId);
    const alerted = stillAlerted(kept, alertTimer, now);
    if (alerted !== kept) writeAlerted(plannedTaskId, alerted);
    moments.current = alertMoments(alertTimer, alerted, now);
    set(moments.current.map(moment => ({ key: `${plannedTaskId}:${moment.checkIn}`, at: moment.at })));
  }, [plannedTaskId, ticking, startedInUtc, timePassed, plannedMinutes, sessionIsUsed, answered, chimeOn, noticeOn]);

  useEffect(() => onNoticeClick(({ plannedTaskId: id }) => noticeClicked(id)), [noticeClicked]);
}
